/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useCallback } from 'react';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import Button from 'components/Button';
import PageContainer from 'components/PageContainer';
import { FormTextField } from 'components/TextField';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import { FormValues } from './OtpVerification';
import styles from './styles';
import InlineButton from 'components/InlineButton';
import BackToPrevious from 'components/unified/BackToPrevious';
import { useFormattedMobilePhone } from 'utils/formatUtils';

type PropTypes = FormikProps<FormValues> & {
  mobilePhone: string;
  resendSms: () => Promise<unknown>;
  attemptsRemaining: string | null;
  tooManyAttempts: boolean;
  timedOut: boolean;
};

const OtpVerificationView: React.FC<PropTypes> = props => {
  const {
    isValid,
    isSubmitting,
    resendSms,
    attemptsRemaining,
    tooManyAttempts,
    timedOut,
    mobilePhone,
    resetForm,
  } = props;

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();

  const handleResend = useCallback(async () => {
    await resendSms();
    resetForm();
  }, [resendSms, resetForm]);

  const formattedMobilePhone = useFormattedMobilePhone(mobilePhone);

  return (
    <Fragment>
      <PageContainer maxWidth="sm">
        <Form noValidate>
          <div css={styles.headingContainer}>
            <Title>{tMd('unified.consumerSearch.otpVerification.title')}</Title>
          </div>
          <div css={styles.body}>
            {tMdMultiline(
              'unified.consumerSearch.otpVerification.instructional',
              { mobilePhone: formattedMobilePhone },
            )}
          </div>
          <div css={styles.formFields}>
            {timedOut ? (
              <div>
                <p css={styles.errorText}>
                  {tMd('unified.consumerSearch.otpVerification.timedOut')}
                </p>
              </div>
            ) : null}
            <Field name="otpCode">
              {(fieldProps: FieldProps) => (
                <FormTextField
                  id="otpCode"
                  fieldProps={fieldProps}
                  label={t(
                    'unified.consumerSearch.otpVerification.formLabel.otpCode',
                  )}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                  }}
                  error={Boolean(attemptsRemaining)}
                  helperText={attemptsRemaining}
                  disabled={tooManyAttempts || timedOut}
                />
              )}
            </Field>
          </div>
          <div css={styles.retryActions}>
            {/** type="button" is to prevent schema validation on click" */}
            <InlineButton onClick={handleResend} type="button">
              {tMd('unified.consumerSearch.otpVerification.resendButton')}
            </InlineButton>
          </div>

          <ActionButtonContainer>
            <Button
              mobileFullWidth={true}
              type="submit"
              id="formSubmit"
              disabled={!isValid || isSubmitting || tooManyAttempts || timedOut}
              loading={isSubmitting}
            >
              {tMd('unified.consumerSearch.otpVerification.submitButton')}
            </Button>
            <BackToPrevious tMdKey="unified.consumerSearch.otpVerification.backButton" />
          </ActionButtonContainer>
        </Form>
      </PageContainer>
    </Fragment>
  );
};

export default OtpVerificationView;
